// ============================================
// Wizard Modal Management
// 마법사 모달 열기/닫기, 단계 이동
// ============================================

import {
  getWizardState,
  getCurrentStep,
  setCurrentStep,
  resetWizardState,
  canProceedToNext,
  getProgressPercent,
  getTotalSteps
} from './state.js';
import { renderStep } from './steps.js';
import { generateFromWizard, calculateWizardCost } from './generate.js';
import { onStepEnter, getImageModelsCache } from './events.js';

// ============================================
// 선택 모달 (빠른 시작 vs 직접 만들기)
// ============================================

export function openCreationMethodModal() {
  const modal = document.getElementById('creationMethodModal');
  if (!modal) {
    console.error('[Wizard] creationMethodModal not found');
    return;
  }

  // 전체화면 모달 - block으로 표시
  modal.style.display = 'block';
  requestAnimationFrame(() => {
    modal.classList.add('active');
  });
  window.ModalManager?.push('creationMethodModal');
}

export function closeCreationMethodModal() {
  const modal = document.getElementById('creationMethodModal');
  if (!modal) return;

  modal.classList.remove('active');
  setTimeout(() => {
    modal.style.display = 'none';
  }, 300);
  window.ModalManager?.remove('creationMethodModal');
}

// ============================================
// 마법사 모달
// ============================================

export function openWizardModal() {
  const modal = document.getElementById('aiWizardModal');
  if (!modal) {
    console.error('[Wizard] aiWizardModal not found');
    return;
  }

  // 상태 초기화
  resetWizardState();

  // 전체화면 모달 - block으로 표시
  modal.style.display = 'block';
  requestAnimationFrame(() => {
    modal.classList.add('active');
  });
  window.ModalManager?.push('aiWizardModal');

  // 첫 단계 렌더링
  renderCurrentStep();
  updateProgress();
  updateNavigationButtons();
}

export function closeWizardModal() {
  const modal = document.getElementById('aiWizardModal');
  if (!modal) return;

  modal.classList.remove('active');
  setTimeout(() => {
    modal.style.display = 'none';
  }, 300);
  window.ModalManager?.remove('aiWizardModal');

  // 상태 초기화
  resetWizardState();
}

// ============================================
// 단계 이동
// ============================================

export function goToNextStep() {
  const currentStep = getCurrentStep();
  const totalSteps = getTotalSteps();

  // 마지막 단계에서는 생성 실행
  if (currentStep >= totalSteps) {
    generateFromWizard();
    return;
  }

  // 현재 단계 유효성 검사
  if (!canProceedToNext()) {
    return;
  }

  const nextStep = currentStep + 1;
  setCurrentStep(nextStep);
  renderCurrentStep();
  updateProgress();
  updateNavigationButtons();
  onStepEnter(nextStep);
}

export function goToPreviousStep() {
  const currentStep = getCurrentStep();

  if (currentStep <= 1) {
    // 첫 단계에서 뒤로가면 선택 모달로 돌아감
    // 선택 모달을 먼저 표시 (애니메이션 없이)
    const methodModal = document.getElementById('creationMethodModal');
    if (methodModal) {
      methodModal.style.display = 'block';
      methodModal.classList.add('active');
      window.ModalManager?.push('creationMethodModal');
    }
    // 그 다음 마법사 모달 페이드 아웃
    closeWizardModal();
    return;
  }

  const prevStep = currentStep - 1;
  setCurrentStep(prevStep);
  renderCurrentStep();
  updateProgress();
  updateNavigationButtons();
  onStepEnter(prevStep);
}

export function goToStep(step) {
  const totalSteps = getTotalSteps();
  if (step < 1 || step > totalSteps) return;

  setCurrentStep(step);
  renderCurrentStep();
  updateProgress();
  updateNavigationButtons();
  onStepEnter(step);
}

// ============================================
// UI 업데이트
// ============================================

export function renderCurrentStep() {
  const container = document.getElementById('wizardStepContent');
  if (!container) return;

  const state = getWizardState();
  container.innerHTML = renderStep(state.currentStep, state);
}

export function updateProgress() {
  const progressBar = document.querySelector('.wizard-progress-bar');
  if (progressBar) {
    progressBar.style.width = `${getProgressPercent()}%`;
  }

  const stepIndicator = document.getElementById('wizardStepIndicator');
  if (stepIndicator) {
    stepIndicator.textContent = `${getCurrentStep()} / ${getTotalSteps()}`;
  }

  // 프로그레스 닷 업데이트
  updateProgressDots();
}

export function updateProgressDots() {
  const currentStep = getCurrentStep();
  const dots = document.querySelectorAll('.wizard-dot');

  dots.forEach((dot, index) => {
    const stepNum = index + 1;
    dot.classList.remove('active', 'completed');

    if (stepNum === currentStep) {
      dot.classList.add('active');
    } else if (stepNum < currentStep) {
      dot.classList.add('completed');
    }
  });
}

export function updateNavigationButtons() {
  const currentStep = getCurrentStep();
  const totalSteps = getTotalSteps();
  const state = getWizardState();

  // 이전 버튼
  const prevBtn = document.getElementById('wizardPrevBtn');
  if (prevBtn) {
    // 첫 단계에서도 버튼은 보이지만, 클릭하면 선택 모달로 돌아감
    prevBtn.disabled = false;
  }

  // 다음/생성 버튼
  const nextBtn = document.getElementById('wizardNextBtn');
  if (nextBtn) {
    if (currentStep >= totalSteps) {
      // 마지막 단계: 생성 버튼 (카본 아이콘 + 숫자)
      const cost = calculateWizardCost(state, getImageModelsCache());
      const carbonIcon = `<svg class="wizard-btn-carbon-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>`;
      if (state.isGenerating) {
        nextBtn.innerHTML = '생성 중...';
        nextBtn.disabled = true;
      } else {
        nextBtn.innerHTML = `생성 ${carbonIcon} ${cost}`;
        nextBtn.disabled = !canProceedToNext();
      }
    } else {
      nextBtn.innerHTML = '다음';
      nextBtn.disabled = !canProceedToNext();
    }
  }
}

// ============================================
// 선택 모달에서 방식 선택
// ============================================

export function selectCreationMethod(method) {
  if (method === 'wizard') {
    // 빠른 시작: 위자드 모달 열기 후 페이드인 완료되면 선택 모달 닫기
    openWizardModal();
    // 위자드 모달의 페이드인 완료 후 (300ms) 선택 모달 닫기
    setTimeout(() => {
      closeCreationMethodModal();
    }, 300);
  } else if (method === 'manual') {
    // 직접 만들기: 에디터 먼저 열고, 페이드인 완료 후 선택 모달 닫기
    import('../editor/modal.js').then(({ openCharacterEditor }) => {
      openCharacterEditor(null);
      // 에디터 모달 페이드인 완료 후 (300ms) 선택 모달 닫기
      setTimeout(() => {
        closeCreationMethodModal();
      }, 300);
    });
  }
}
